import { Point, Wall, Room, UtilityElement } from '../types';
import { distanceToLine, calculateArea } from './floorPlanUtils';
import { PIXELS_PER_METER } from '../constants/floorPlanConstants';

const GARDEN_SNAP_DISTANCE = 40; // pixels - distance to snap to walls

/**
 * Snaps a garden element to nearby walls to create a rectangular or L-shaped garden area
 */
export function snapGardenToWalls(
  gardenPosition: Point,
  walls: (Wall & { tempId?: string; stableId?: string })[],
  rooms: Room[],
  currentDimensions?: { width: number; height: number }
): {
  snappedWalls: string[];
  width: number;
  height: number;
  area: number;
  isLShaped: boolean;
  corners?: Point[]; // For L-shaped gardens
} {
  // Find all walls within snapping distance
  const nearbyWalls = walls.filter(wall => {
    const distance = distanceToLine(gardenPosition, wall.start, wall.end);
    return distance < GARDEN_SNAP_DISTANCE;
  });

  if (nearbyWalls.length === 0) {
    // No walls nearby - use default dimensions
    const width = currentDimensions?.width || 300;
    const height = currentDimensions?.height || 200;
    return {
      snappedWalls: [],
      width,
      height,
      area: (width / PIXELS_PER_METER) * (height / PIXELS_PER_METER),
      isLShaped: false
    };
  }

  // Group walls by orientation (horizontal vs vertical)
  const horizontalWalls = nearbyWalls.filter(wall => 
    Math.abs(wall.end.y - wall.start.y) < Math.abs(wall.end.x - wall.start.x)
  );
  const verticalWalls = nearbyWalls.filter(wall => 
    Math.abs(wall.end.x - wall.start.x) < Math.abs(wall.end.y - wall.start.y)
  );

  // Find room that contains this point to determine boundaries
  const containingRoom = rooms.find(room => {
    return room.points && isPointInsidePolygon(gardenPosition, room.points);
  });

  // Calculate garden bounds based on nearby walls and room boundaries
  if (containingRoom && containingRoom.points) {
    // Try to create L-shaped garden if room shape allows
    const lShapeResult = detectLShapeOpportunity(gardenPosition, containingRoom, nearbyWalls);
    if (lShapeResult.isLShaped) {
      return lShapeResult;
    }
  }

  // Fallback to rectangular garden
  let width = currentDimensions?.width || 300;
  let height = currentDimensions?.height || 200;

  // Snap to wall boundaries
  if (horizontalWalls.length >= 2) {
    const wallYs = horizontalWalls.map(w => (w.start.y + w.end.y) / 2);
    height = Math.abs(Math.max(...wallYs) - Math.min(...wallYs));
  }
  if (verticalWalls.length >= 2) {
    const wallXs = verticalWalls.map(w => (w.start.x + w.end.x) / 2);
    width = Math.abs(Math.max(...wallXs) - Math.min(...wallXs));
  }

  return {
    snappedWalls: nearbyWalls.map(w => w.stableId || w.tempId || '').filter(Boolean),
    width,
    height,
    area: (width / PIXELS_PER_METER) * (height / PIXELS_PER_METER),
    isLShaped: false
  };
}

/**
 * Detects if room shape allows for L-shaped garden
 */
function detectLShapeOpportunity(
  position: Point,
  room: Room,
  walls: (Wall & { tempId?: string; stableId?: string })[]
): {
  snappedWalls: string[];
  width: number;
  height: number;
  area: number;
  isLShaped: boolean;
  corners?: Point[];
} {
  // Check if room has an L-shape by analyzing corners
  const points = room.points || [];
  if (points.length < 6) {
    // Not enough points for L-shape
    return { snappedWalls: [], width: 0, height: 0, area: 0, isLShaped: false };
  }

  // Detect concave corners (indicating potential L-shape)
  const concaveCorners = findConcaveCorners(points);
  
  if (concaveCorners.length > 0) {
    // Room has L-shape potential - calculate garden to fill the area using polygon area
    const area = calculatePolygonArea(points);
    const areaM2 = area / (PIXELS_PER_METER * PIXELS_PER_METER);
    
    return {
      snappedWalls: walls.map(w => w.stableId || w.tempId || '').filter(Boolean),
      width: 0, // L-shaped gardens don't have simple width/height
      height: 0,
      area: areaM2,
      isLShaped: true,
      corners: points
    };
  }

  return { snappedWalls: [], width: 0, height: 0, area: 0, isLShaped: false };
}

/**
 * Finds concave corners in a polygon (indicating L-shapes)
 */
function findConcaveCorners(points: Point[]): Point[] {
  const concaveCorners: Point[] = [];
  
  for (let i = 0; i < points.length; i++) {
    const prev = points[(i - 1 + points.length) % points.length];
    const curr = points[i];
    const next = points[(i + 1) % points.length];
    
    // Calculate cross product to determine if corner is concave
    const crossProduct = 
      (curr.x - prev.x) * (next.y - curr.y) - 
      (curr.y - prev.y) * (next.x - curr.x);
    
    if (crossProduct < 0) {
      concaveCorners.push(curr);
    }
  }
  
  return concaveCorners;
}

/**
 * Calculate area of polygon using shoelace formula
 */
export function calculatePolygonArea(points: Point[]): number {
  if (points.length < 3) return 0;
  
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  return Math.abs(area / 2);
}

/**
 * Calculate center (centroid) of a polygon
 */
export function calculatePolygonCenter(points: Point[]): Point {
  if (points.length === 0) return { x: 0, y: 0 };
  
  const sumX = points.reduce((sum, p) => sum + p.x, 0);
  const sumY = points.reduce((sum, p) => sum + p.y, 0);
  
  return {
    x: sumX / points.length,
    y: sumY / points.length
  };
}

/**
 * Point-in-polygon test using ray casting
 */
export function isPointInsidePolygon(point: Point, polygon: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y;
    const xj = polygon[j].x, yj = polygon[j].y;
    
    const intersect = ((yi > point.y) !== (yj > point.y))
      && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}
